import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FileUploadsFilterMenu from "./FileUploadsFilterMenu";
import FileUploadsJobApplicationListing from "./FileUploadsJobApplicationListing";
import FileUploadModal from "./FileUploadModal";
import { Job } from "../../../../Entities/Job";
import { FileUploadsJobFilters, PaginationType } from "../../../../Interface";
import bulkUploadService from "../../../../Service/bulk.upload.service";
import useAccess from "../../../../Hooks/useAccess";
import { message } from "antd";

interface FileUploadsProps {
  jobDescriptionId: string;
}

const FileUploads: React.FC<FileUploadsProps> = ({
  jobDescriptionId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FileUploadsJobFilters>({
    status: null,
    failureReason: null,
    sort: null,
  });

  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
  });

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);

  const { currentSubscription } = useAccess();

  const handleFileUploadClick = () => {
    if (!currentSubscription) {
      return;
    }

    if (currentSubscription.status === "inactive") {
      message.error("Your subscription is inactive. Please upgrade your subscription to upload files.");
        return;
    }

    setShowFileUploadModal(true);
  };

  const handleModalClose = () => {
    setShowFileUploadModal(false);
  };

  const handleNewJobAdd = (job: Job | null) => {
    if (!job) return;

    setJobs((prev) => [job, ...prev]);
    setPagination((prev) => ({
      ...prev,
      totalRecords: prev.totalRecords + 1,
    }));
  };

  const handleFilterChange = (
    key: keyof FileUploadsJobFilters,
    value: any | null,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: null,
      failureReason: null,
      sort: null,
    });
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize || prev.limit,
    }));
  };

  useEffect(() => {
    const fetchJobs = async () => {
      const response = await bulkUploadService.getAllFileUploadsRecords(
        jobDescriptionId,
        filters,
        pagination,
      );
      setJobs(response.data);
      setPagination((prev) => ({
        ...prev,
        totalRecords: response.total,
        totalPages: response.totalPages,
      }));
    };

    fetchJobs();
  }, [pagination.page, pagination.limit, filters, jobDescriptionId]);

  useEffect(() => {
    const openUpload = searchParams.get("openUpload");
    if (openUpload === "true") {
      setShowFileUploadModal(true);
      // Remove the parameter from URL after opening the modal
      searchParams.delete("openUpload");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <section className="file-uploads-section">
      <FileUploadsFilterMenu
        handleFileUploadClick={handleFileUploadClick}
        handleFilterChange={handleFilterChange}
        clearFilters={clearFilters}
        filters={filters}
      />
      <FileUploadsJobApplicationListing
        jobDescriptionId={jobDescriptionId}
        jobs={jobs}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
      />
      <FileUploadModal
        handleModalClose={handleModalClose}
        showFileUploadModal={showFileUploadModal}
        setShowFileUploadModal={setShowFileUploadModal}
        jobDescriptionId={jobDescriptionId}
        handleNewJobAdd={handleNewJobAdd}
      />
    </section>
  );
};

export default FileUploads;
